import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { motion } from 'framer-motion';
import { CATEGORIES, MONTHS, categoryOptions } from './constants';

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    background: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '0.75rem',
    color: 'white',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(255, 255, 255, 0.1)' : 'none',
    '&:hover': { background: 'rgba(255, 255, 255, 0.15)', borderColor: 'rgba(255, 255, 255, 0.3)' },
    transition: 'all 0.3s ease',
    minHeight: '44px',
    cursor: 'pointer',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? 'rgba(255, 255, 255, 0.2)' : state.isFocused ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
    color: 'white',
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
    padding: '0.75rem 1rem',
    cursor: 'pointer',
  }),
  menu: (provided) => ({
    ...provided,
    background: 'rgba(0, 0, 0, 0.9)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '0.75rem',
    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
    zIndex: 9999,
  }),
  menuList: (provided) => ({ ...provided, padding: '0.5rem' }),
  singleValue: (provided) => ({ ...provided, color: 'white' }),
  placeholder: (provided) => ({ ...provided, color: 'rgba(255, 255, 255, 0.7)' }),
  dropdownIndicator: (provided) => ({ ...provided, color: 'rgba(255, 255, 255, 0.7)', '&:hover': { color: 'white' } }),
  clearIndicator: (provided) => ({ ...provided, color: 'rgba(255, 255, 255, 0.7)', '&:hover': { color: 'white' } }),
  input: (provided) => ({ ...provided, color: 'white' }),
  menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
};

function ExpenseSearch() {
  const [query, setQuery] = useState('');
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedWho, setSelectedWho] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [totalShown, setTotalShown] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const latestRequest = useRef(0);
  const [expandedCards, setExpandedCards] = useState([]);

  const currentYear = new Date().getFullYear();

  const yearOptions = useMemo(() => {
    const options = [{ value: null, label: 'All Years' }];
    for (let y = currentYear; y >= 2020; y--) {
      options.push({ value: y.toString(), label: y.toString() });
    }
    return options;
  }, [currentYear]);

  const whoOptions = useMemo(() => [
    { value: 'Daniel', label: 'Daniel' },
    { value: 'Yehudit', label: 'Yehudit' },
  ], []);

  const fetchResults = useCallback(async () => {
    const params = {};
    if (query.trim()) params.q = query.trim();
    if (selectedYear) params.year = selectedYear;
    if (selectedCategory) params.category = selectedCategory;
    if (selectedWho) params.who = selectedWho;

    // At least one filter is required by the API
    if (Object.keys(params).length === 0) {
      setResults([]);
      setTotalShown(0);
      return;
    }

    // Only the latest search may update results — a slow earlier response must not overwrite it
    const requestId = ++latestRequest.current;
    setLoading(true);
    try {
      const response = await axios.get('/api/search-expenses', {
        params,
        withCredentials: true,
      });
      if (requestId !== latestRequest.current) return;

      const expenses = response.data?.expenses || [];
      setResults(expenses);
      setTotalShown(expenses.length);
      setTruncated(!!response.data?.truncated);
    } catch (err) {
      if (requestId !== latestRequest.current) return;
      console.error('Search failed:', err);
      setResults([]);
      setTotalShown(0);
      setTruncated(false);
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [query, selectedYear, selectedCategory, selectedWho]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchResults();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchResults]);

  const handleClearFilters = () => {
    setQuery('');
    setSelectedYear(null);
    setSelectedCategory(null);
    setSelectedWho(null);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedResults = useMemo(() => {
    const sorted = [...results];
    sorted.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === 'amount') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (sortField === 'date') {
        // Sort on the parsed ISO date — date text like "9/30" vs "10/3" doesn't sort as strings
        aVal = a.parsedDate || '';
        bVal = b.parsedDate || '';
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [results, sortField, sortDirection]);

  const toggleCardExpand = (index) => {
    setExpandedCards(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const renderSortArrow = (field) => {
    if (sortField !== field) {
      return (
        <svg className="w-3 h-3 ml-1 opacity-30 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    if (sortDirection === 'asc') {
      return (
        <svg className="w-3 h-3 ml-1 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
        </svg>
      );
    }
    return (
      <svg className="w-3 h-3 ml-1 inline-block" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  const columns = [
    { key: 'date', label: 'Date', align: 'left' },
    { key: 'amount', label: 'Amount', align: 'right' },
    { key: 'reason', label: 'Reason', align: 'left' },
    { key: 'category', label: 'Category', align: 'left' },
    { key: 'paymentType', label: 'Payment', align: 'left' },
    { key: 'who', label: 'Who', align: 'left' },
  ];

  const formatAmount = (amount) => {
    const num = parseFloat(amount);
    if (isNaN(num)) return amount;
    return num.toLocaleString();
  };

  const hasActiveFilters = query || selectedYear || selectedCategory || selectedWho;

  // Compute stats from current results
  const stats = useMemo(() => {
    if (results.length === 0) return null;
    const totalAmount = results.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);
    const years = [...new Set(results.map(e => e.year).filter(Boolean))].sort();
    const danielTotal = results.filter(e => e.who === 'Daniel').reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
    const yehuditTotal = results.filter(e => e.who === 'Yehudit' || e.who === 'yehudit').reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
    const avgPerExpense = totalAmount / results.length;

    // Top 3 categories by total
    const catTotals = {};
    results.forEach(e => {
      if (e.category) catTotals[e.category] = (catTotals[e.category] || 0) + (parseFloat(e.amount) || 0);
    });
    const topCategories = Object.entries(catTotals).sort((a, b) => b[1] - a[1]).slice(0, 3);

    // By year breakdown
    const byYear = {};
    results.forEach(e => {
      if (e.year) byYear[e.year] = (byYear[e.year] || 0) + (parseFloat(e.amount) || 0);
    });

    // Top month
    const byMonth = {};
    results.forEach(e => {
      const key = `${e.month} ${e.year}`;
      byMonth[key] = (byMonth[key] || 0) + (parseFloat(e.amount) || 0);
    });
    const topMonth = Object.entries(byMonth).sort((a, b) => b[1] - a[1])[0];

    return { totalAmount, years, danielTotal, yehuditTotal, avgPerExpense, topCategories, byYear, topMonth };
  }, [results]);

  return (
    <div className="min-h-screen p-4 md:p-6">
      <div className="w-full max-w-7xl mx-auto">
        {/* Header */}
        <motion.header
          className="mb-8 glass glass-hover rounded-2xl p-6 text-center animate-slideInLeft"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center justify-center mb-4">
            <div className="w-12 h-12 glass rounded-xl flex items-center justify-center mr-3">
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white">Expense Search</h1>
          </div>
          <p className="text-white/80">Search and filter through all your expenses</p>
        </motion.header>

        {/* Filter Bar */}
        <motion.div
          className="glass glass-hover rounded-2xl p-6 mb-6 animate-fadeIn"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {/* Search Text Input */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-white/90 mb-2">Search</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by reason, category, or any keyword..."
                  className="glass-input w-full pl-12 pr-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-white/30 transition-all duration-300"
                />
              </div>
            </div>

            {/* Year Dropdown */}
            <div>
              <label className="block text-sm font-medium text-white/90 mb-2">Year</label>
              <Select
                value={yearOptions.find(o => o.value === selectedYear) || yearOptions[0]}
                onChange={(option) => setSelectedYear(option ? option.value : null)}
                options={yearOptions}
                styles={customStyles}
                placeholder="All Years"
                classNamePrefix="react-select"
                isClearable={false}
                menuPortalTarget={document.getElementById('dropdown-portal')}
                menuPosition="fixed"
              />
            </div>

            {/* Category Dropdown */}
            <div>
              <label className="block text-sm font-medium text-white/90 mb-2">Category</label>
              <Select
                value={categoryOptions.find(o => o.value === selectedCategory) || null}
                onChange={(option) => setSelectedCategory(option ? option.value : null)}
                options={categoryOptions}
                styles={customStyles}
                placeholder="All Categories"
                classNamePrefix="react-select"
                isClearable
                menuPortalTarget={document.getElementById('dropdown-portal')}
                menuPosition="fixed"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
            {/* Who Dropdown */}
            <div>
              <label className="block text-sm font-medium text-white/90 mb-2">Who</label>
              <Select
                value={whoOptions.find(o => o.value === selectedWho) || null}
                onChange={(option) => setSelectedWho(option ? option.value : null)}
                options={whoOptions}
                styles={customStyles}
                placeholder="Everyone"
                classNamePrefix="react-select"
                isClearable
                menuPortalTarget={document.getElementById('dropdown-portal')}
                menuPosition="fixed"
              />
            </div>

            {/* Clear All Filters Button */}
            <div>
              <button
                onClick={handleClearFilters}
                disabled={!hasActiveFilters}
                className="glass-button w-full py-3 px-4 text-white font-medium rounded-xl transition-all duration-300 flex justify-center items-center disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear All Filters
              </button>
            </div>
          </div>
        </motion.div>

        {/* Stats Card */}
        {!loading && stats && (
          <motion.div
            className="glass rounded-2xl p-6 mb-6"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.15 }}
          >
            {/* Top row: key metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white">
                  {'\u20AA'}{stats.totalAmount.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                </div>
                <div className="text-xs text-white/60 mt-1">Total Amount</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white">{results.length}</div>
                <div className="text-xs text-white/60 mt-1">Expenses</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white">
                  {'\u20AA'}{stats.avgPerExpense.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                </div>
                <div className="text-xs text-white/60 mt-1">Avg per Expense</div>
              </div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white">
                  {stats.years.length > 1 ? `${stats.years[0]}-${stats.years[stats.years.length - 1]}` : stats.years[0] || '-'}
                </div>
                <div className="text-xs text-white/60 mt-1">Year{stats.years.length > 1 ? 's' : ''}</div>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-white/10 my-4"></div>

            {/* Bottom row: breakdowns */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Daniel vs Yehudit split */}
              {(stats.danielTotal > 0 || stats.yehuditTotal > 0) && (
                <div>
                  <div className="text-xs text-white/50 uppercase tracking-wider mb-2">Split</div>
                  <div className="space-y-1.5">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-white/80">Daniel</span>
                      <span className="text-sm font-medium text-white">{'\u20AA'}{stats.danielTotal.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-1.5">
                      <div className="bg-blue-400 h-1.5 rounded-full" style={{ width: `${(stats.danielTotal / stats.totalAmount * 100)}%` }}></div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-white/80">Yehudit</span>
                      <span className="text-sm font-medium text-white">{'\u20AA'}{stats.yehuditTotal.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-1.5">
                      <div className="bg-purple-400 h-1.5 rounded-full" style={{ width: `${(stats.yehuditTotal / stats.totalAmount * 100)}%` }}></div>
                    </div>
                  </div>
                </div>
              )}

              {/* Top categories */}
              {stats.topCategories.length > 0 && (
                <div>
                  <div className="text-xs text-white/50 uppercase tracking-wider mb-2">Top Categories</div>
                  <div className="space-y-1.5">
                    {stats.topCategories.map(([cat, total]) => (
                      <div key={cat} className="flex justify-between items-center">
                        <span className="text-sm text-white/80 truncate mr-2">{cat}</span>
                        <span className="text-sm font-medium text-white flex-shrink-0">{'\u20AA'}{total.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* By year */}
              {Object.keys(stats.byYear).length > 1 && (
                <div>
                  <div className="text-xs text-white/50 uppercase tracking-wider mb-2">By Year</div>
                  <div className="space-y-1.5">
                    {Object.entries(stats.byYear).sort((a, b) => b[0] - a[0]).slice(0, 4).map(([yr, total]) => (
                      <div key={yr} className="flex justify-between items-center">
                        <span className="text-sm text-white/80">{yr}</span>
                        <span className="text-sm font-medium text-white">{'\u20AA'}{total.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Peak month (show when single year or few results) */}
              {Object.keys(stats.byYear).length <= 1 && stats.topMonth && (
                <div>
                  <div className="text-xs text-white/50 uppercase tracking-wider mb-2">Peak Month</div>
                  <div className="text-sm text-white/80">{stats.topMonth[0]}</div>
                  <div className="text-lg font-semibold text-white">{'\u20AA'}{stats.topMonth[1].toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Results Count */}
        {!loading && (
          <motion.div
            className="mb-4 px-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <p className="text-white/70 text-sm font-medium">
              Showing {totalShown} result{totalShown !== 1 ? 's' : ''}{truncated ? ' (limit reached — narrow your search to see everything)' : ''}
            </p>
          </motion.div>
        )}

        {/* Results Area */}
        <motion.div
          className="glass-table rounded-2xl overflow-hidden"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <div className="p-6">
            {loading ? (
              /* Loading State */
              <div className="py-16 text-center">
                <svg className="animate-spin h-10 w-10 text-white mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="mt-4 text-white/80">Searching expenses...</p>
              </div>
            ) : sortedResults.length === 0 ? (
              /* Empty State */
              <div className="py-16 text-center">
                <div className="w-16 h-16 glass rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">No expenses found</h3>
                <p className="text-white/60 text-sm">Try adjusting your search terms or filters</p>
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-white/20">
                    <thead>
                      <tr>
                        {columns.map(col => (
                          <th
                            key={col.key}
                            onClick={() => handleSort(col.key)}
                            className={`px-4 py-3 text-xs font-medium text-white/70 uppercase tracking-wider cursor-pointer select-none hover:text-white transition-colors duration-200 ${
                              col.align === 'right' ? 'text-right' : 'text-left'
                            }`}
                          >
                            <span className="inline-flex items-center">
                              {col.label === 'Amount' ? 'Amount (\u20AA)' : col.label}
                              {renderSortArrow(col.key)}
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {sortedResults.map((entry, index) => (
                        <motion.tr
                          key={index}
                          className="hover:bg-white/5 transition-colors duration-200"
                          initial={{ opacity: 0, y: 5 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.2, delay: Math.min(index * 0.02, 0.5) }}
                        >
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-white/90">{entry.date}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-white/90 text-right font-medium">
                            {'\u20AA'}{formatAmount(entry.amount)}
                          </td>
                          <td className="px-4 py-4 text-sm text-white/90 max-w-xs truncate">{entry.reason}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-white/90">{entry.category}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-white/90">{entry.paymentType}</td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-white/90">{entry.who}</td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden space-y-3">
                  {sortedResults.map((entry, index) => {
                    const isExpanded = expandedCards.includes(index);
                    return (
                      <motion.div
                        key={index}
                        className="glass rounded-xl overflow-hidden"
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2, delay: Math.min(index * 0.03, 0.5) }}
                      >
                        {/* Card Preview - always visible */}
                        <div
                          className="p-4 flex justify-between items-center cursor-pointer"
                          onClick={() => toggleCardExpand(index)}
                        >
                          <div className="flex flex-col min-w-0 mr-3">
                            <span className="font-medium text-white truncate">{entry.reason}</span>
                            <span className="text-sm text-white/70">{entry.date} - {entry.category}</span>
                          </div>
                          <div className="flex flex-col items-end flex-shrink-0">
                            <span className="font-semibold text-white">{'\u20AA'}{formatAmount(entry.amount)}</span>
                            <svg
                              className={`w-4 h-4 text-white/50 mt-1 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>

                        {/* Expanded Details */}
                        {isExpanded && (
                          <div className="px-4 pb-4 pt-2 border-t border-white/20 bg-white/5">
                            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                              <div className="text-white/60">Payment</div>
                              <div className="text-white/90">{entry.paymentType}</div>

                              <div className="text-white/60">Who</div>
                              <div className="text-white/90">{entry.who}</div>

                              <div className="text-white/60">Category</div>
                              <div className="text-white/90">{entry.category}</div>

                              <div className="text-white/60">Amount</div>
                              <div className="text-white/90">{'\u20AA'}{formatAmount(entry.amount)}</div>
                            </div>
                          </div>
                        )}

                        {/* Visual expand indicator */}
                        <div className="h-1 w-full bg-white/10 flex justify-center">
                          <div className="w-8 h-1 rounded-full bg-white/30"></div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default ExpenseSearch;
